/**
 * Product Certifications - JavaScript Controller
 * Handles badge tooltips and document table interactions
 */

class ProductCertifications extends HTMLElement {
  constructor() {
    super();
    this.badges = [];
    this.tooltips = [];
  }

  connectedCallback() {
    this.initTabs();
    this.initBadgeTooltips();
    this.initDocumentTable();
  }

  initTabs() {
    const tabs = this.querySelectorAll('.cert-tab');
    const tabContents = this.querySelectorAll('.cert-tab-content');
    
    if (tabs.length === 0) return; // No tabs in this layout

    tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        
        const targetId = tab.getAttribute('data-tab');
        
        // Update tabs
        tabs.forEach((t) => {
          t.classList.remove('cert-tab--active');
          t.setAttribute('aria-selected', 'false');
          t.setAttribute('tabindex', '-1');
        });
        
        tab.classList.add('cert-tab--active');
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');
        
        // Update content
        tabContents.forEach((content) => {
          content.classList.remove('is-active');
          content.setAttribute('aria-hidden', 'true');
        });
        
        const targetContent = this.querySelector(`#${targetId}`);
        if (targetContent) {
          targetContent.classList.add('is-active');
          targetContent.setAttribute('aria-hidden', 'false');
        }
      });

      // Keyboard navigation
      tab.addEventListener('keydown', (e) => {
        let nextTab = null;
        
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          const currentIndex = Array.from(tabs).indexOf(tab);
          nextTab = tabs[currentIndex + 1] || tabs[0];
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          const currentIndex = Array.from(tabs).indexOf(tab);
          nextTab = tabs[currentIndex - 1] || tabs[tabs.length - 1];
        } else if (e.key === 'Home') {
          e.preventDefault();
          nextTab = tabs[0];
        } else if (e.key === 'End') {
          e.preventDefault();
          nextTab = tabs[tabs.length - 1];
        }
        
        if (nextTab) {
          nextTab.click();
          nextTab.focus();
        }
      });
    });
  }

  initBadgeTooltips() {
    this.badges = Array.from(this.querySelectorAll('.product-certifications__badge'));
    
    this.badges.forEach((badge) => {
      const tooltip = badge.querySelector('.product-certifications__badge-tooltip');
      
      if (!tooltip) return;

      // Show tooltip on hover (desktop)
      badge.addEventListener('mouseenter', () => {
        tooltip.classList.add('is-visible');
      });

      badge.addEventListener('mouseleave', () => {
        tooltip.classList.remove('is-visible');
      });

      // Toggle tooltip on click/tap (mobile)
      badge.addEventListener('click', (e) => {
        // Don't prevent default if it's a link
        if (!badge.querySelector('a')) {
          e.preventDefault();
        }
        
        // Hide all other tooltips
        this.badges.forEach((otherBadge) => {
          if (otherBadge !== badge) {
            const otherTooltip = otherBadge.querySelector('.product-certifications__badge-tooltip');
            if (otherTooltip) {
              otherTooltip.classList.remove('is-visible');
            }
          }
        });

        // Toggle current tooltip
        tooltip.classList.toggle('is-visible');
      });

      // Accessibility: keyboard support
      badge.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          tooltip.classList.toggle('is-visible');
        }
        if (e.key === 'Escape') {
          tooltip.classList.remove('is-visible');
        }
      });
    });

    // Close tooltips when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.product-certifications__badge')) {
        this.badges.forEach((badge) => {
          const tooltip = badge.querySelector('.product-certifications__badge-tooltip');
          if (tooltip) {
            tooltip.classList.remove('is-visible');
          }
        });
      }
    });
  }

  initDocumentTable() {
    const downloadButtons = this.querySelectorAll('.certifications-table__action a');
    
    downloadButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        // Track download event if analytics available
        if (window.gtag) {
          const docName = button.closest('tr').querySelector('.certifications-table__name span')?.textContent;
          window.gtag('event', 'file_download', {
            file_name: docName,
            file_extension: button.closest('tr').querySelector('.format-badge')?.textContent
          });
        }

        // Add visual feedback
        button.classList.add('is-downloading');
        setTimeout(() => {
          button.classList.remove('is-downloading');
        }, 2000);
      });
    });
  }
}

customElements.define('product-certifications', ProductCertifications);

/**
 * Initialize certifications sections on page load
 */
document.addEventListener('DOMContentLoaded', () => {
  const certificationsElements = document.querySelectorAll('.product-certifications');
  
  certificationsElements.forEach((element) => {
    // No additional initialization needed - Web Component handles it
  });
});
